<template>
  <footer v-if="!isWebviewUserAgent" class="footer">
    <div class="footer__container">
      <template v-if="minimal">
        <p class="footer__disclaimers-copyright">
          {{ $t('onboarding_copyright_reserved') }}
        </p>
      </template>
      <template v-else>
        <div class="footer__links">
          <div class="footer__links-legal">
            <sword-link
              data-testid="link_termsAndConditions"
              :text="$t('onboarding.footer_terms_new')"
              :href="termsAndConditionsUrl"
              variant="secondary"
              is-quiet
            />
            <sword-link
              v-if="showHipaaConsent"
              data-testid="link_hipaaConsent"
              :text="$t('onboarding.footer_hipaa_new')"
              :href="hipaaUrl"
              variant="secondary"
              is-quiet
            />
            <sword-link
              data-testid="link_privacyStatement"
              :text="$t('onboarding.footer_privacystatement_new')"
              :href="privacyStatementUrl"
              variant="secondary"
              is-quiet
            />
            <sword-link
              v-if="showNonDiscriminationConsent"
              data-testid="link_nonDiscrimination"
              :text="$t('onboarding_footer_non_discrimination_new')"
              :href="nonDescriminationUrl"
              variant="secondary"
              is-quiet
            />
          </div>
          <div class="footer__links-help">
            <span>{{ $t('onboarding_help_email_label') }}</span>
            <sword-link
              data-testid="link_supportEmail"
              :text="supportEmail"
              :href="supportMailTo"
              variant="primary"
              is-quiet
            />
          </div>
        </div>
        <div class="footer__disclaimers">
          <div class="footer__disclaimers-copyright">
            <p data-testid="text_copyrightDisclaimer">
              {{ $t('onboarding_copyright_reserved') }}
            </p>
          </div>
          <div class="footer__disclaimers-legal">
            <p data-testid="text_legalDisclaimer">
              {{ $t('onboarding_disclaimer') }}
            </p>
          </div>
        </div>
      </template>
    </div>
  </footer>
</template>

<script>
import { mapGetters } from 'vuex';
import SwordLink from '@ui-kit/components/links/SwordLink.vue';
import { isWebview } from '@/scripts/webview';

import { CONFIGS } from '@/scripts/constants';
import { env } from '@/config';
import { CONFIGURATIONS } from '@/scripts/constants/configurations';
import { clientStoreTypes, applicationStoreTypes } from '@/store/types';
import eco from '@/mixins/eco';

export default {
  name: 'SWORDFooter',
  components: {
    SwordLink,
  },
  mixins: [eco],
  props: {
    links: {
      type: Object,
      default: () => ({}),
    },
    minimal: Boolean,
  },
  data() {
    return {
      termsOfUseURL: env.VUE_APP_TERMS_OF_USE_URL,
      noticeOfPrivacyURL: env.VUE_APP_NOTICE_OF_PRIVACY_URL,
      informedConsentURL: env.VUE_APP_INFORMED_CONSENT_URL,
      nonDiscriminationURL: env.VUE_APP_NON_DISCRIMINATION_URL,
    };
  },
  computed: {
    ...mapGetters({
      clientLegalLinks: clientStoreTypes.getters.GET_CLIENT_LEGAL_LINKS,
      clientConfiguration: clientStoreTypes.getters.GET_CLIENT_CONFIGURATION,
      supportEmailByUnit: applicationStoreTypes.getters.SUPPORT_EMAIL_BY_UNIT,
      unit: applicationStoreTypes.getters.UNIT,
    }),
    termsAndConditionsUrl() {
      const { clientLegalLinks, termsOfUseURL } = this;
      return clientLegalLinks?.termsAndConditions ?? termsOfUseURL;
    },
    hipaaUrl() {
      const { clientLegalLinks, noticeOfPrivacyURL } = this;
      return clientLegalLinks?.hipaaConsent ?? noticeOfPrivacyURL;
    },
    privacyStatementUrl() {
      const { clientLegalLinks, informedConsentURL } = this;
      return clientLegalLinks?.privacyStatement ?? informedConsentURL;
    },
    nonDescriminationUrl() {
      const { clientLegalLinks, nonDiscriminationURL } = this;
      return clientLegalLinks?.nonDiscrimination ?? nonDiscriminationURL;
    },
    isWebviewUserAgent() {
      return isWebview(navigator.userAgent);
    },
    supportEmail() {
      if (this.isEcoExperience) {
        return this.clientConfiguration(CONFIGURATIONS.SUPPORT_EMAIL) || 'help@swordhealth.com';
      }

      return this.supportEmailByUnit;
    },
    supportMailTo() {
      return `mailto:${this.supportEmail}`;
    },
    showHipaaConsent() {
      return CONFIGS.VUE_APP_SHOW_HIPAA_CONSENT === 'true';
    },
    showNonDiscriminationConsent() {
      return CONFIGS.VUE_APP_SHOW_NON_DISCRIMINATION_CONSENT === 'true';
    },
  },
  methods: {
    hideLink(name) {
      return this.linksToHide.includes(name);
    },
  },
};
</script>

<style lang="scss" scoped>
.footer {
  background: $background-color-dark;

  &__container {
    width: 100%;
    max-width: $screen-xl-wuk;
    padding: $space-lg $space-sm;
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    gap: $space-md;
    margin: 0 auto;

    @media screen and (min-width: $desktop-size) {
      padding: $space-lg $space-xl;
      gap: $space-lg;
    }
  }

  p {
    margin: 0;
  }

  &__links {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-md;
    width: 100%;

    @media screen and (min-width: $desktop-size) {
      flex-direction: row;
      justify-content: space-between;
    }

    &-legal {
      display: flex;
      flex-direction: column;
      gap: $space-sm;
      align-items: center;

      ::v-deep .link {
        @extend %t4;
        @extend %-t_medium;
      }

      @media screen and (min-width: $desktop-size) {
        flex-direction: row;
        gap: $space-md;
      }
    }

    &-help {
      display: flex;
      gap: $space-xs;
      align-items: center;
      @extend %t4;
      @extend %-t_regular;
    }
  }

  &__disclaimers {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-md;
    text-align: center;
    width: 100%;

    @media screen and (min-width: $desktop-size) {
      flex-direction: row-reverse;
      justify-content: space-between;
      text-align: left;
      align-items: flex-start;
    }

    &-copyright {
      @extend %t4;
      @extend %-t_regular;
      white-space: nowrap;
    }

    &-legal {
      @extend %-t_regular;
      font-size: 0.635rem;
      line-height: 1rem;
    }
  }
}
</style>
